import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useForm, Controller } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Select,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { displayPatientName } from "../../utils/displayPatientName";
import { usePatientContext } from "../../context/PatientContext";

const BORDER = "#d0d5dd";
const NAVY = "#1d2a4d";

const SALUTATIONS = ["Mr", "Mrs", "Ms", "Master", "Baby", "Dr"];
const GENDER_OPTIONS = [
  { value: "M", label: "M" },
  { value: "F", label: "F" },
  { value: "O", label: "Other" },
];

/** Approximate DOB (YYYY-MM-DD) from an age value + unit, subtracting from today. */
function estimateDobFromAge(value, unit) {
  const n = value === "" ? NaN : parseInt(value, 10);
  if (Number.isNaN(n) || n < 0 || n > 150) return null;
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  if (unit === "Months") d.setMonth(d.getMonth() - n);
  else d.setFullYear(d.getFullYear() - n);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Whole years from a YYYY-MM-DD date of birth. */
function computeAgeYearsFromDob(dateString) {
  if (!dateString) return null;
  const birthDate = new Date(dateString);
  if (Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  if (birthDate > today) return 0;
  let years = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    years -= 1;
  }
  return years < 0 ? 0 : years;
}
/**
 * Quick "Add New Patient" modal. Collects only the mandatory fields and creates
 * the patient via add_patient/. State/District are derived silently from the Pin
 * code. Each footer action registers the patient, sets it as the app-wide active
 * patient and routes to Rx / Bill / Appointment.
 */
function QuickRegisterPatientModal({ open, onClose }) {
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  const authToken = sessionStorage.getItem("accessToken") ?? "";
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${authToken}` }),
    [authToken],
  );
  const history = useHistory();
  const { selectPatient } = usePatientContext();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      salutation: "",
      name: "",
      phoneNumber: "",
      alternatePhoneNumber: "",
      gender: "",
      dateOfBirth: "",
      city: "",
      locality: "",
      pincode: "",
      state: "",
      district: "",
    },
  });

  const [states, setStates] = useState([]);
  const [age, setAge] = useState("");
  const [ageUnit, setAgeUnit] = useState("Years");
  const [lastAgeEdit, setLastAgeEdit] = useState(null); // "age" | "dob"
  const [dobType, setDobType] = useState("text"); // swap to "date" on focus for the placeholder
  const [submitting, setSubmitting] = useState(false);
  const today = new Date().toISOString().split("T")[0];

  const watchedPincode = watch("pincode");
  const watchedDob = watch("dateOfBirth");

  // Cache the states list (used only to resolve the pincode's state name).
  useEffect(() => {
    if (!open || !authToken) return;
    axios
      .get(`${REACT_APP_BACKEND_ROUTE_URL}get_all_states/`, { headers })
      .then((res) => setStates(res.data?.state_list || []))
      .catch((err) => console.error("Failed to load states:", err));
  }, [open, authToken, REACT_APP_BACKEND_ROUTE_URL, headers]);

  // Derive state & district silently from a valid 6-digit pincode.
  // useEffect(() => {
  //   if (!watchedPincode || !/^\d{6}$/.test(watchedPincode)) return;
  //   axios
  //     .get(
  //       `${REACT_APP_BACKEND_ROUTE_URL}get_pincode_details/?pincode=${watchedPincode}`,
  //       { headers },
  //     )
  //     .then((res) => {
  //       if (
  //         res.data?.status === "Success" &&
  //         res.data?.pincode_details?.length > 0
  //       ) {
  //         const details = res.data.pincode_details[0];
  //         if (details.statename)
  //           setValue("state", details.statename.toLowerCase());
  //         if (details.district)
  //           setValue("district", details.district.toLowerCase());
  //         if (!getValues("city") && details.district)
  //           setValue("city", details.district);
  //       }
  //     })
  //     .catch((err) => console.error("Error fetching pincode details:", err));
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [watchedPincode]);

  // Age -> DOB
  useEffect(() => {
    if (lastAgeEdit !== "age") return;
    const dob = estimateDobFromAge(age, ageUnit);
    if (dob) setValue("dateOfBirth", dob, { shouldValidate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [age, ageUnit, lastAgeEdit]);

  // DOB -> Age
  useEffect(() => {
    if (lastAgeEdit !== "dob" || !watchedDob) return;
    const years = computeAgeYearsFromDob(watchedDob);
    if (years !== null) {
      setAge(String(years));
      setAgeUnit("Years");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedDob, lastAgeEdit]);

  const handleClose = () => {
    reset();
    setAge("");
    setAgeUnit("Years");
    setLastAgeEdit(null);
    setDobType("text");
    setSubmitting(false);
    onClose();
  };

  const submit = (action) => (data) => {
    setSubmitting(true);
    const [yearOfBirth, monthOfBirth, dayOfBirth] = (
      data.dateOfBirth || ""
    ).split("-");
    const patientName = (data.name || "").trim();

    const payload = {
      salutation: data.salutation,
      name: patientName,
      patientName,
      gender: data.gender,
      dateOfBirth: data.dateOfBirth,
      yearOfBirth,
      monthOfBirth,
      dayOfBirth,
      phoneNumber: data.phoneNumber,
      // Optional. Sent even when blank so clearing it persists.
      alternatePhoneNumber: data.alternatePhoneNumber ?? "",
      city: data.city,
      locality: data.locality,
      pincode: data.pincode,
      state: data.state,
      district: data.district,
      country: "India",
      transaction_id_internal: "",
    };

    axios
      .post(
        `${REACT_APP_BACKEND_ROUTE_URL}add_patient/`,
        JSON.stringify(payload),
        { headers },
      )
      .then((response) => {
        if (response.data?.message?.includes("successfully")) {
          const created = response.data.patient || {};
          const patientId = created.patient_id;
          toast.success(
            `${response.data.message} Patient Name: ${displayPatientName(
              patientName,
            )}${patientId ? ` , Patient ID: ${patientId}` : ""}`,
          );

          const navPatient = {
            ...created,
            patient_id: patientId,
            pat_id: patientId,
            name: patientName,
            patient_name: patientName,
            gender: data.gender,
            dob: data.dateOfBirth,
            phone_no: data.phoneNumber,
            // Read-side shape, mirroring phone_no.
            alternate_phone_no: data.alternatePhoneNumber ?? "",
          };

          selectPatient(navPatient);

          if (patientId) {
            axios
              .post(
                `${REACT_APP_BACKEND_ROUTE_URL}add_patient_static_data/`,
                {
                  patient_id: patientId,
                  hip_id: sessionStorage.getItem("facility_name"),
                  insurance_schemes: [],
                },
                { headers },
              )
              .catch((err) =>
                console.error("Error adding patient static data:", err),
              );
          }

          handleClose();
          if (action === "bill") {
            // Open the add-invoice page directly with the patient preselected.
            history.push({
              pathname: "/newInvoice",
              state: { preselectedInvoicePatient: navPatient },
            });
          } else if (action === "appointment") {
            history.push({
              pathname: "/doctor/appointments",
              state: { preselectedAppointmentPatient: navPatient },
            });
          }
        } else {
          const message =
            response.data?.message || "An unknown error occurred.";
          if (response.data?.patient) {
            toast.error(
              `${message} Patient Name: ${displayPatientName(
                response.data.patient.name,
              )}  Patient ID: ${response.data.patient.patient_id}`,
            );
          } else {
            toast.error(message);
          }
          setSubmitting(false);
        }
      })
      .catch((error) => {
        const rawMessage =
          error?.response?.data?.message?.message ??
          error?.response?.data?.message ??
          error?.response?.data?.detail ??
          error?.message;
        const extractedValidationMessage =
          typeof rawMessage === "string"
            ? rawMessage.match(/['"]name['"]\s*:\s*\[\s*['"]([^'"]+)['"]\s*\]/)?.[1]
            : null;
        const message =
          extractedValidationMessage ||
          (typeof rawMessage === "string"
            ? rawMessage
            : "Unable to process request.");
        toast.error(message);
        setSubmitting(false);
      });
  };

  const labelSx = {
    fontWeight: 600,
    fontSize: "1rem",
    color: NAVY,
    mb: 1,
  };
  // Roomy light-bordered field, matching the design.
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#fff",
      "& fieldset": { borderColor: BORDER },
      "&:hover fieldset": { borderColor: "#98a2b3" },
      "&.Mui-focused fieldset": { borderColor: NAVY },
    },
    "& .MuiInputBase-input::placeholder": { color: "#98a2b3", opacity: 1 },
  };
  // Composite outlined container (salutation + name, age + unit).
  const outlinedBox = {
    display: "flex",
    alignItems: "center",
    border: `1px solid ${BORDER}`,
    borderRadius: "10px",
    backgroundColor: "#fff",
    minHeight: 56,
    "&:hover": { borderColor: "#98a2b3" },
  };
  const errorText = (msg) =>
    msg ? (
      <Typography color="error" sx={{ fontSize: "0.8rem", mt: 0.5 }}>
        {msg}
      </Typography>
    ) : null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px" } }}
    >
      <DialogTitle
        sx={{
          backgroundColor: "#213360",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          className="main-title"
          style={{
            textAlign: "center",
            color: "#FFFFFF",
            margin: "0px",
            fontSize: "20px",
          }}
        >
          Quick Registration
        </div>
        <IconButton onClick={handleClose}>
          <CloseIcon
            sx={{
              color: "#FFFFFF",
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
            }}
          />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3} sx={{ mt: 0, paddingTop: 0 }}>
          {/* Patient Name (salutation + name) */}
          <Grid item xs={12} sm={7}>
            <Typography sx={labelSx}>Patient Name*</Typography>
            <Box sx={outlinedBox}>
              <Controller
                name="salutation"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    variant="standard"
                    disableUnderline
                    displayEmpty
                    sx={{ px: 2, minWidth: 72 }}
                    renderValue={(v) => v || " "}
                  >
                    {SALUTATIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <Divider orientation="vertical" flexItem />
              <Controller
                name="name"
                control={control}
                rules={{ required: "Patient Name is required." }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    variant="standard"
                    placeholder="Enter Name"
                    InputProps={{
                      disableUnderline: true,
                      startAdornment: (
                        <InputAdornment position="start" sx={{ pl: 1.5 }}>
                          <PersonOutlineIcon sx={{ color: "#98a2b3" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& input::placeholder": { color: "#98a2b3", opacity: 1 } }}
                  />
                )}
              />
            </Box>
            {errorText(errors.name?.message)}
          </Grid>

          {/* Phone Number */}
          <Grid item xs={12} sm={5}>
            <Typography sx={labelSx}>Phone Number*</Typography>
            <Controller
              name="phoneNumber"
              control={control}
              rules={{
                required: "Phone Number is required.",
                pattern: {
                  value: /^[0-9]{10}$/,
                  message: "Phone Number must be 10 digits.",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="Enter Number"
                  sx={fieldSx}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CallOutlinedIcon sx={{ color: "#98a2b3" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Gender */}
          <Grid item xs={12} sm={3}>
            <Typography sx={labelSx}>Gender*</Typography>
            <Controller
              name="gender"
              control={control}
              rules={{ required: "Gender is required." }}
              render={({ field }) => (
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  value={field.value}
                  onChange={(e, val) => val && field.onChange(val)}
                  sx={{
                    height: 56,
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      borderColor: BORDER,
                      color: "#475467",
                      fontWeight: 500,
                      fontSize: "0.95rem",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "#e8eefc !important",
                      color: `${NAVY} !important`,
                      fontWeight: 700,
                      borderColor: `${NAVY} !important`,
                    },
                  }}
                >
                  {GENDER_OPTIONS.map((g) => (
                    <ToggleButton key={g.value} value={g.value}>
                      {g.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
            />
            {errorText(errors.gender?.message)}
          </Grid>

          {/* Age or DOB */}
          <Grid item xs={12} sm={9}>
            <Typography sx={labelSx}>Age or DOB*</Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              {/* Age + unit */}
              <Box sx={{ ...outlinedBox, flex: 1 }}>
                <TextField
                  type="number"
                  placeholder="Age"
                  value={age}
                  onChange={(e) => {
                    setAge(e.target.value);
                    setLastAgeEdit("age");
                  }}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    px: 2,
                    width: 72,
                    "& input::placeholder": { color: "#98a2b3", opacity: 1 },
                  }}
                />
                <Divider orientation="vertical" flexItem />
                <Select
                  value={ageUnit}
                  onChange={(e) => {
                    setAgeUnit(e.target.value);
                    setLastAgeEdit("age");
                  }}
                  variant="standard"
                  disableUnderline
                  sx={{ px: 2, flex: 1 }}
                >
                  <MenuItem value="Years">Years</MenuItem>
                  <MenuItem value="Months">Months</MenuItem>
                </Select>
              </Box>
              {/* DOB */}
              <Controller
                name="dateOfBirth"
                control={control}
                rules={{
                  required: "Age or DOB is required.",
                  validate: (value) =>
                    !value ||
                    value <= today ||
                    "Date of Birth cannot be in the future.",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type={dobType}
                    placeholder="DOB"
                    sx={{ ...fieldSx, flex: 1 }}
                    inputProps={{ max: today }}
                    onFocus={() => setDobType("date")}
                    onBlur={(e) => {
                      if (!e.target.value) setDobType("text");
                    }}
                    onChange={(e) => {
                      field.onChange(e);
                      setLastAgeEdit("dob");
                    }}
                    error={!!errors.dateOfBirth}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayOutlinedIcon sx={{ color: "#98a2b3" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            </Box>
            {errorText(errors.dateOfBirth?.message)}
          </Grid>

          {/* Address — full width */}
          <Grid item xs={12}>
            <Typography sx={labelSx}>Address*</Typography>
            <Controller
              name="locality"
              control={control}
              rules={{ required: "Address is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  minRows={1}
                  placeholder="Enter Address"
                  sx={fieldSx}
                  error={!!errors.locality}
                  helperText={errors.locality?.message}
                />
              )}
            />
          </Grid>

          {/* City + Pin — half / half */}
          <Grid item xs={12} sm={6}>
            <Typography sx={labelSx}>City</Typography>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="Enter City"
                  sx={fieldSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={labelSx}>Pin*</Typography>
            <Controller
              name="pincode"
              control={control}
              rules={{
                required: "Pin Code is required.",
                pattern: {
                  value: /^[0-9]{6}$/,
                  message: "Pin Code must be 6 digits.",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="Enter Pin"
                  sx={fieldSx}
                  error={!!errors.pincode}
                  helperText={errors.pincode?.message}
                />
              )}
            />
          </Grid>

          {/* Alternate Phone — optional */}
          <Grid item xs={12} sm={6}>
            <Typography sx={labelSx}>Alternate Phone Number</Typography>
            <Controller
              name="alternatePhoneNumber"
              control={control}
              rules={{
                validate: (value) =>
                  !value ||
                  /^[0-9]{10}$/.test(value) ||
                  "Alternate Phone Number must be 10 digits.",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="Enter Number (optional)"
                  sx={fieldSx}
                  error={!!errors.alternatePhoneNumber}
                  helperText={errors.alternatePhoneNumber?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CallOutlinedIcon sx={{ color: "#98a2b3" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "flex-end", gap: 2, pb: 3, pt: 1 }}>
        <Button
          variant="contained"
          size="small"
          disabled={submitting}
          onClick={handleSubmit(submit("bill"))}
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            backgroundColor: "#121959",
            color: "#fff",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.5, sm: 1 },
            "&:hover": { backgroundColor: "#121959" },
          }}
        >
          Add &amp; Create Bill
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={submitting}
          onClick={handleSubmit(submit("appointment"))}
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            backgroundColor: "#121959",
            color: "#fff",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.5, sm: 1 },
            "&:hover": { backgroundColor: "#121959" },
          }}
        >
          Add &amp; Create Appointment
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={submitting}
          onClick={handleSubmit(submit("add"))}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : null
          }
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            backgroundColor: "#121959",
            color: "#fff",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.5, sm: 1 },
            "&:hover": { backgroundColor: "#121959" },
          }}
        >
          Add Patient
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default QuickRegisterPatientModal;
